// 帳號 VMS 事件中心（MV.2，docs/mycam/mv-spec.md ③）：GET /v3/me/events 的用戶端。
// 帳號底下所有設備的事件，伺服器端合併、篩選、分頁；facets 每一維套「其他維」的篩選。
import { apiFetch } from '../api';
import type { EventItem } from '../../features/events/eventsLib';

/** AI 標籤篩選值（同 api service.MyEventLabelKeys）。unconfirmed = 事件驗證閘道判「未確認」（payload.verify），跟 AI 電腦的判斷可並存；muted = 在不通知區（docs/mute-zones.md）。 */
export const MY_EVENT_LABELS = ['person', 'pet', 'vehicle', 'package', 'false_positive', 'uncertain', 'unjudged', 'unconfirmed', 'muted'] as const;
export type MyEventLabel = (typeof MY_EVENT_LABELS)[number];

export interface MyEvent extends EventItem {
  device_id: string;
}

export interface MyEventFacets {
  device: Record<string, number>;
  label: Record<string, number>;
  type: Record<string, number>;
  /** 鍵 = 那一小時開頭的 µs（字串） */
  hour: Record<string, number>;
}

export interface MyEventsPage {
  items: MyEvent[];
  facets: MyEventFacets;
  total: number;
  next_cursor: string;
  limit: number;
}

export interface MyEventsQuery {
  fromUs: number;
  toUs: number;
  devices?: string[];
  labels?: string[];
  types?: string[];
  cursor?: string;
  limit?: number;
}

const csv = (xs?: string[]) => (xs && xs.length ? xs.join(',') : undefined);

export async function fetchMyEvents(q: MyEventsQuery): Promise<MyEventsPage> {
  const res = await apiFetch<MyEventsPage>('/v3/me/events', {
    query: {
      from: q.fromUs,
      to: q.toUs,
      devices: csv(q.devices),
      labels: csv(q.labels),
      types: csv(q.types),
      cursor: q.cursor || undefined,
      limit: q.limit,
    },
  });
  const d = res.data;
  return {
    items: d?.items ?? [],
    facets: d?.facets ?? { device: {}, label: {}, type: {}, hour: {} },
    total: d?.total ?? 0,
    next_cursor: d?.next_cursor ?? '',
    limit: d?.limit ?? 0,
  };
}

/** 點事件 → 多台同步回放停在事件前 5 秒（K：focus + at − 5~10 s）。 */
export const PLAYBACK_LEAD_US = 5_000_000;
export function playbackHref(ev: Pick<MyEvent, 'device_id' | 'event_ts'>): string {
  return `/playback/multi?ids=${encodeURIComponent(ev.device_id)}&t=${ev.event_ts - PLAYBACK_LEAD_US}`;
}

/** 時段：近 24 小時 / 近 7 天 / 近 30 天 / 指定日。 */
export type RangeKey = '24h' | '7d' | '30d' | 'day';
const H = 3_600_000_000;
export function rangeWindow(key: RangeKey, nowUs: number, dayWindow: { fromUs: number; toUs: number }): { fromUs: number; toUs: number } {
  switch (key) {
    case '24h':
      return { fromUs: nowUs - 24 * H, toUs: nowUs + 60_000_000 };
    case '7d':
      return { fromUs: nowUs - 7 * 24 * H, toUs: nowUs + 60_000_000 };
    case '30d':
      return { fromUs: nowUs - 30 * 24 * H, toUs: nowUs + 60_000_000 };
    default:
      return dayWindow;
  }
}

/** 每小時筆數 → 時段內連續的長條（沒事件的小時補 0）；超過 maxBars 就合併成較粗的桶。 */
export function hourBars(hour: Record<string, number>, w: { fromUs: number; toUs: number }, maxBars = 168): { fromUs: number; count: number }[] {
  const first = w.fromUs - (((w.fromUs % H) + H) % H);
  const n = Math.max(1, Math.ceil((w.toUs - first) / H));
  const per = Math.max(1, Math.ceil(n / maxBars));
  const bars: { fromUs: number; count: number }[] = [];
  for (let i = 0; i < n; i += per) bars.push({ fromUs: first + i * H, count: 0 });
  for (const [k, c] of Object.entries(hour)) {
    const idx = Math.floor((Number(k) - first) / H / per);
    if (idx >= 0 && idx < bars.length) bars[idx].count += c;
  }
  return bars;
}
